import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CalendarCheck, ClipboardCheck, ClockAlert, Contact, KeyRound, Puzzle, Settings, ShieldAlert, TicketPercent, Users } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ErrorBoundary from "@/components/ErrorBoundary";
import AttendanceTab from "@/components/tabs/AttendanceTab";
import LatenessTab from "@/components/tabs/LatenessTab";
import AccountabilityTab from "@/components/tabs/AccountabilityTab";
import DeductionsTab from "@/components/tabs/DeductionsTab";
import PerformanceTab from "@/components/tabs/PerformanceTab";
import EmployeesTab from "@/components/tabs/EmployeesTab";
import StaffPortalTab from "@/components/tabs/StaffPortalTab";
import SettingsTab from "@/components/tabs/SettingsTab";
import ExtensionTab from "@/components/tabs/ExtensionTab";

const PERM_TABS = [
  { id: "attendance", perm: "attendance", label: "سجل الدوام", icon: CalendarCheck, testId: "staff-console-tab-attendance", C: AttendanceTab },
  { id: "lateness", perm: "reports", label: "التقارير", icon: ClockAlert, testId: "staff-console-tab-reports", C: LatenessTab },
  { id: "accountability", perm: "inquiries", label: "المسائلات", icon: ShieldAlert, testId: "staff-console-tab-inquiries", C: AccountabilityTab },
  { id: "deductions", perm: "deductions", label: "قرارات الحسم", icon: TicketPercent, testId: "staff-console-tab-deductions", C: DeductionsTab },
  { id: "employees", perm: "employees", label: "الموظفون", icon: Users, testId: "staff-console-tab-employees", C: EmployeesTab },
  { id: "portal", perm: "circulars", label: "التعاميم", icon: Contact, testId: "staff-console-tab-circulars", C: StaffPortalTab },
  { id: "performance", perm: "performance", label: "تقييم الأداء الوظيفي", icon: ClipboardCheck, testId: "staff-console-tab-performance", C: PerformanceTab },
  { id: "settings", perm: "settings", label: "الإعدادات", icon: Settings, testId: "staff-console-tab-settings", C: SettingsTab },
  { id: "extension", perm: "extension", label: "إضافة أداة الكروم", icon: Puzzle, testId: "staff-console-tab-extension", C: ExtensionTab },
];

export default function StaffConsole() {
  const navigate = useNavigate();
  const [employee] = useState(() => {
    try { return JSON.parse(localStorage.getItem("staff_employee") || "null"); } catch { return null; }
  });
  const token = localStorage.getItem("staff_token");
  const [perms, setPerms] = useState(null);
  const [tab, setTab] = useState("");

  useEffect(() => {
    if (!token || !employee) { navigate("/staff", { replace: true }); return undefined; }
    localStorage.setItem("staff_mirror", "1");
    let live = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff/permissions`, { headers: { "X-Staff-Token": token } })
      .then(async (r) => {
        if (!live) return;
        if (r.status === 401) {
          localStorage.removeItem("staff_token");
          localStorage.removeItem("staff_employee");
          navigate("/staff", { replace: true });
          return;
        }
        const d = await r.json();
        const p = d.permissions || {};
        setPerms(p);
        const first = PERM_TABS.find((t) => p[t.perm]);
        if (first) setTab(first.id);
      })
      .catch(() => { if (live) navigate("/staff/home", { replace: true }); });
    return () => { live = false; localStorage.removeItem("staff_mirror"); };
    // eslint-disable-next-line
  }, []);

  const allowed = perms ? PERM_TABS.filter((t) => perms[t.perm]) : [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="staff-console">
      <header className="bg-emerald-950 text-white sticky top-0 z-20 shadow-lg">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-900 flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h1 className="font-extrabold text-base sm:text-lg leading-tight" data-testid="staff-console-name">{employee?.name || ""}</h1>
              <p className="text-[11px] text-emerald-300/70 hidden sm:block">التبويبات المفوَّضة — راصد حضوري</p>
            </div>
          </div>
          <button onClick={() => navigate("/staff/home")} data-testid="staff-console-back"
            className="flex items-center gap-1.5 text-xs sm:text-sm bg-emerald-900 hover:bg-emerald-800 rounded-lg px-3 py-2 transition-colors">
            <ArrowRight className="w-4 h-4" /> عودة لبوابتي
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {perms === null && <p className="text-center text-sm text-slate-400 py-20">جارٍ التحميل...</p>}
        {perms !== null && allowed.length === 0 && (
          <div className="bg-white rounded-2xl border p-8 text-center space-y-2 max-w-md mx-auto" data-testid="staff-console-empty">
            <KeyRound className="w-8 h-8 text-slate-300 mx-auto" />
            <p className="text-sm font-bold text-slate-600">لا تملك أي صلاحيات مفوَّضة حالياً</p>
            <p className="text-xs text-slate-400">تواصل مع مدير المدرسة لمنحك صلاحية الوصول للتبويبات</p>
          </div>
        )}
        {allowed.length > 0 && (
          <ErrorBoundary>
            <Tabs value={tab} onValueChange={setTab} dir="rtl">
              <TabsList className="w-full justify-start flex-wrap h-auto gap-1 bg-white border p-1.5 rounded-xl mb-6" data-testid="staff-console-tabs">
                {allowed.map(({ id, label, icon: Icon, testId }) => (
                  <TabsTrigger key={id} value={id} data-testid={testId}
                    className="flex items-center gap-2 data-[state=active]:bg-emerald-900 data-[state=active]:text-white rounded-lg px-3 py-2 text-xs sm:text-sm">
                    <Icon className="w-4 h-4" /> {label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {allowed.map(({ id, C }) => (
                <TabsContent key={id} value={id}><C /></TabsContent>
              ))}
            </Tabs>
          </ErrorBoundary>
        )}
      </main>
    </div>
  );
}
